/* eslint-disable */
import { useRef, useMemo, useEffect, useState, type JSX } from 'react';

import * as React from 'react';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import LegacyImage from 'next/legacy/image';
import dynamic from 'next/dynamic';
import Svg from 'lib/svgs.json';
import withVisibleHoc, {
  WithVisibleHocProps,
} from 'components/misc/visible-hoc';
import MutableNextLink from '../reusable/mutable-next-line';
import SiteAccordion from './site-accordion';

import RichTextRenderer from '../misc/rich-text-renderer';
import { trackEvent } from 'lib/analytics';
import cloudinaryLoader from 'lib/image/cloudinary-loader';
import {
  ContentSection,
  getContentSection,
} from 'lib/contentful/get-content-section';
import { getContentFieldClass } from 'lib/contentful/get-content-field-class';
import { getContentField } from 'lib/contentful/get-content-field';
import { getContentFieldType } from 'lib/contentful/get-content-field-type';
import { isScreenLarge } from 'lib/utils/screen-size';
import { createMarkup } from 'lib/utils/create-markup';
import { buttonEventHandler } from 'lib/utils/button-event-handler';
import { showPopup } from 'lib/utils/show-popup';
import styled from 'styled-components';

const getContainerFieldClass = (data): string => {
  const classes = data?.containerClass || data?.fields?.containerClass || [];
  return ['container'].concat(classes).join(' ') || '';
};

const SiteSwiper = dynamic(() => import('./site-swiper'));
const Vimeo = dynamic(() => import('@u-wave/react-vimeo'));
const MuxPlayer = dynamic(
  () => import('components/reusable/MuxPlayer/mux-player'),
);

const TableHeader = styled.th`
  text-align: left;
  padding: 20px;
`;

export type SiteSectionsProps = {
  data: {
    section?: ContentSection[];
  };
  include?: string;
  exclude?: string[];
  onVisible?: Function;
  location: string;
};

export default function SiteSections({
  data,
  include,
  exclude,
  onVisible,
  location,
}: SiteSectionsProps): JSX.Element {
  const sections = getContentSection({ data, include, exclude }) || [];
  return (
    <>
      {sections.map((v, i) => {
        const withAnchor = getContentFieldClass(v).includes('with-anchor');
        const title = v?.fields?.title;
        return (
          <Section
            key={i}
            // @ts-ignore
            index={i}
            data={v}
            title={title}
            location={location}
            withAnchor={withAnchor}
            onVisible={onVisible}
          />
        );
      })}
    </>
  );
}

type SectionHocPropsectionProps = {
  data: ContentSection;
  index: number;
  title: string;
  location: string;
};

type SectionHocProps = WithVisibleHocProps<SectionHocPropsectionProps>;

export const Section = withVisibleHoc(
  // @ts-ignore
  (
    { data, onVisible, title, index, location }: SectionHocProps,
    ref: React.MutableRefObject<HTMLElement>,
  ) => {
    const fields = getContentField({ data }) || [];

    useEffect(() => {
      ref.current.querySelectorAll('p:empty').forEach((v) => v.remove());
    }, []);

    return (
      <section
        ref={ref}
        data-title={title}
        className={getContentFieldClass(data)}
      >
        <div className={getContainerFieldClass(data)}>
          {fields.map((v, i) => (
            <Field
              sectionIndex={index}
              key={title + i}
              data={v}
              onVisible={onVisible}
              location={location}
            />
          ))}
        </div>
      </section>
    );
  },
);

export const Field = ({ data, onVisible, sectionIndex, location }) => {
  const title = data?.fields?.title || '';
  const type = getContentFieldType(data);

  const withAnchor = getContentFieldClass(data).includes('with-anchor');

  switch (type) {
    case 'accordion':
      return <AccordionEl data={data} title={title} />;
    case 'button':
      return <ButtonEl data={data} title={title} location={location} />;
    case 'heading':
      return (
        // @ts-ignore
        <HeadingEl
          data={data}
          title={title}
          withAnchor={withAnchor}
          onVisible={onVisible}
          sectionIndex={sectionIndex}
        />
      );
    case 'paragraph':
      return (
        // @ts-ignore
        <ParagraphEl
          data={data}
          withAnchor={withAnchor}
          title={title}
          onVisible={onVisible}
          location={location}
        />
      );
    case 'poster':
      return (
        // @ts-ignore
        <PosterEl
          checkOnlyVisible
          withAnchor={withAnchor}
          onVisible={onVisible}
          data={data}
          title={title}
        />
      );
    case 'slider':
      // @ts-ignore
      return <SliderEl data={data} title={title} />;
    case 'small':
      return <SmallEl data={data} title={title} />;
    case 'table':
      // @ts-ignore
      return <TableEl data={data} title={title} firstRowHeader />;
    case 'section':
      // @ts-ignore
      return <Section data={data} title={title} />;
    default:
      return <div />;
  }
};

export const HeadingEl: any = withVisibleHoc(
  // @ts-ignore
  ({ data, title, sectionIndex }, ref) => {
    const content = data?.paragraph || data?.fields?.paragraph || null;
    const classes = getContentFieldClass(data);

    return (
      <div
        // @ts-ignore
        ref={ref}
        data-title={title}
        data-type='heading'
        className={classes}
        // dangerouslySetInnerHTML={createMarkup(documentToHtmlString(content))}
      >
        {classes.includes('with-number') && (
          <span className='num'>
            {sectionIndex < 10 ? `0${sectionIndex}` : sectionIndex}
          </span>
        )}
        {/* @ts-ignore */}
        <RichTextRenderer content={content} />
      </div>
    );
  },
);

export const ParagraphEl: any = withVisibleHoc(
  (
    {
      // @ts-ignore
      data,
      // @ts-ignore
      title,
      // @ts-ignore
      location,
    },
    ref,
  ) => {
    const content = data?.paragraph || data?.fields?.paragraph || null;

    const fields = getContentField({ data }) || [];

    // TOOLTIP
    const [toolTipActive, setToolTipActive] = useState(false);
    const tooltip = data?.tooltip || data?.fields?.tooltip || null;
    const withTooltip = false;
    const handleTooltipCLickOpen = () => {
      const myEvent = new CustomEvent('tooltip_toggle');
      window.dispatchEvent(myEvent);
      if (!toolTipActive) handleTooltipToggle({ on: true });
    };
    const handleTooltipClickExit = () => {
      const myEvent = new CustomEvent('tooltip_toggle');
      window.dispatchEvent(myEvent);
    };
    const handleTooltipResize = () => {
      if (isScreenLarge()) {
        const myEvent = new CustomEvent('tooltip_toggle');
        window.dispatchEvent(myEvent);
      }
    };
    const handleTooltipToggle = ({ on }) => {
      // @ts-ignore
      ref.current.parentNode.classList.remove('with-tooltip-active');
      // TOOLTIP OPEN
      if (on) {
        setToolTipActive(true);
        // @ts-ignore
        ref?.current?.parentNode?.classList?.add('with-tooltip-active');
        if (!isScreenLarge()) {
          const SiteContent = document.querySelector('.site-content');
          // @ts-ignore
          toolTipWindow.current?.setAttribute('orphan', '');

          SiteContent?.insertAdjacentElement(
            // @ts-ignore
            'beforeEnd',
            toolTipWindow.current,
          );
        }
      }
      // TOOLTIP CLOSE
      else {
        setToolTipActive(false);
        const withOrphan = document.querySelector('[orphan]');
        if (withOrphan) {
          // @ts-ignore
          ref?.current?.insertAdjacentElement(
            'beforeEnd',
            toolTipWindow.current,
          );

          // @ts-ignore
          toolTipWindow.current?.removeAttribute('orphan');
        }
      }
    };

    const toolTipIconExit = useRef(undefined);
    const toolTipIconOpen = useRef(undefined);
    const toolTipWindow = useRef(undefined);

    useEffect(() => {
      if (withTooltip) {
        // @ts-ignore
        ref?.current?.insertAdjacentElement(
          'beforeEnd',
          toolTipIconOpen.current,
        );
        // @ts-ignore
        ref?.current?.insertAdjacentElement('beforeEnd', toolTipWindow.current);

        // @ts-ignore
        toolTipWindow?.current?.insertAdjacentElement(
          'beforeEnd',
          toolTipIconExit.current,
        );

        // @ts-ignore
        window.addEventListener('tooltip_toggle', handleTooltipToggle);
        window.addEventListener('site_resize', handleTooltipResize);
      }
      return () => {
        // Anything in here is fired on component unmount.
        if (withTooltip) {
          // @ts-ignore
          window.removeEventListener('tooltip_toggle', handleTooltipToggle);
          window.removeEventListener('site_resize', handleTooltipResize);
          const orphan = document.querySelector('[orphan]');
          orphan && orphan.remove();
        }
      };
    }, []);

    return (
      <>
        <div
          // @ts-ignore
          ref={ref}
          data-title={title}
          data-type='paragraph'
          className={
            getContentFieldClass(data) +
            (withTooltip ? ' with-tooltip' : '') +
            (toolTipActive ? ' active' : '')
          }
          // dangerouslySetInnerHTML={createMarkup(documentToHtmlString(content).replace(/\n/g, `</br>`))}
        >
          {/* @ts-ignore */}
          <RichTextRenderer content={content} />
          {fields.map((v, i) => (
            // @ts-ignore
            <Field key={title + i} data={v} location={location} />
          ))}
        </div>
        {withTooltip && (
          <>
            <a
              ref={toolTipIconOpen}
              onClick={handleTooltipCLickOpen}
              className={`tooltip-icon-open${toolTipActive ? ' active' : ''}`}
              href='#'
            >
              <i
                className='icon'
                dangerouslySetInnerHTML={createMarkup(Svg.plus_circle)}
              />
            </a>
            <a
              ref={toolTipIconExit}
              onClick={handleTooltipClickExit}
              className='tooltip-icon-exit'
              href='#'
            >
              <i
                className='icon'
                dangerouslySetInnerHTML={createMarkup(Svg.exit)}
              />
            </a>
            <div
              ref={toolTipWindow}
              className={`tooltip-window${toolTipActive ? ' active' : ''}`}
              dangerouslySetInnerHTML={createMarkup(
                documentToHtmlString(tooltip).replace(/\n/g, '</br>'),
              )}
            />
          </>
        )}
      </>
    );
  },
);

type ButtonElProps = {
  title?: string;
  data: any;
  location?: any;
};

export const ButtonEl = ({ data, title, location }: ButtonElProps) => {
  const classList = getContentFieldClass(data);
  const label = data?.fields?.label || 'Submit';
  const href = data?.fields?.link || '/';
  const modal = data?.fields?.modal;
  const icon = data?.fields?.icon;
  const altIcon = data?.fields?.altIcon;
  const id = data?.fields?.id;

  const handleClick = (location) => {
    buttonEventHandler(label, location);
  };
  return (
    <div data-title={label} data-type='button' data-id={id || modal}>
      {(modal && (
        <button
          type='button'
          className={`${classList} modal-button`}
          onClick={() => {
            showPopup(modal);
            trackEvent('Button Clicked', {
              buttonName: 'Sustainability Learn More',
              location: 'Sustainability Module',
            });
          }}
          id={`popup-source-button-${modal}`}
        >
          {icon && <i dangerouslySetInnerHTML={createMarkup(Svg[icon])} />}
          {altIcon && (
            <i dangerouslySetInnerHTML={createMarkup(Svg[altIcon])} />
          )}
          <span>{label}</span>
        </button>
      )) || (
        <MutableNextLink
          href={href}
          prefetch={false}
          onClick={() => handleClick(location)}
          className={classList}
        >
          {icon && <i dangerouslySetInnerHTML={createMarkup(Svg[icon])} />}
          <span>{label}</span>
        </MutableNextLink>
      )}
    </div>
  );
};

export const AccordionEl = ({ data, title }) => {
  const fields = getContentField({ data }) || [];
  const classList = getContentFieldClass(data) || [];
  return (
    <SiteAccordion title={title} classList={classList}>
      {fields.map((v, i) => (
        // @ts-ignore
        <Field key={title + i} data={v} />
      ))}
    </SiteAccordion>
  );
};

// @ts-ignore
export const PosterEl: any = withVisibleHoc(({ data, title, visible }, ref) => {
  const videoId = data?.fields?.videoId;
  const mobileVideoId = data?.fields?.mobileVideoId;
  const desktopPlaybackId =
    process.env.NEXT_PUBLIC_STAGING === 'true'
      ? data?.fields?.devDesktopPlaybackId
      : data?.fields?.desktopPlaybackId;
  const mobilePlaybackId =
    process.env.NEXT_PUBLIC_STAGING === 'true'
      ? data?.fields?.devMobilePlaybackId
      : data?.fields?.mobilePlaybackId;
  const legend = data?.fields?.legend || null;
  const images = data?.image || data?.fields?.image || null;
  const imgLen = images?.length || 0;

  const [vimeoId, setVimeoId] = useState(mobileVideoId);
  const [paused, setPaused] = useState(true);
  const [playbackId, setPlaybackId] = useState(mobilePlaybackId);

  const posterText = legend || title || null;
  const inline = data?.inline || data?.fields?.inline || false;
  const reg = new RegExp('([^-—]+)');

  const figText = posterText && posterText.match(reg)[0];
  const figTextLen = figText?.length || 0;
  const numeric = getContentFieldClass(data).includes('with-numeric');

  const updVideo = () => {
    setVimeoId(isScreenLarge() ? videoId : mobileVideoId);
    setPlaybackId(isScreenLarge() ? desktopPlaybackId : mobilePlaybackId);
  };

  useEffect(() => {
    if (visible) {
      setPaused(false);
    }
  }, [visible]);

  useEffect(() => {
    updVideo();
    window.addEventListener('site_resize', updVideo);
    return () => {
      window.removeEventListener('site_resize', updVideo);
    };
  }, []);

  if (!images && !vimeoId && !playbackId) {
    return null;
  }

  return (
    <div
      data-title={title}
      data-type='poster'
      data-inline={inline}
      // @ts-ignore
      ref={ref}
      className={getContentFieldClass(data)}
    >
      <div className='container'>
        <div className={`grid with-${imgLen}`}>
          <div className='container'>
            {images &&
              !vimeoId &&
              !playbackId &&
              images.map((e, k) => {
                const reg = new RegExp('(/svg/|.svg)');
                const isSVG = e.url.match(reg) && e.url.match(reg)[0];
                const svgUrl = e.url.replace(
                  '/f_auto,q_auto/',
                  '/fl_sanitize/',
                );

                const altText =
                  data?.fields?.altText?.trim() !== ''
                    ? data.fields.altText
                    : title;

                if (inline) {
                  return numeric ? (
                    <div className='numeric-wrap' key={k + title}>
                      <LegacyImage
                        width={e.width}
                        height={e.height}
                        src={isSVG ? svgUrl : e.secure_url}
                        layout='responsive'
                        alt={altText || `seed ${title}`}
                        loader={cloudinaryLoader}
                      />
                      <span className='numeric'>{k + 1}</span>
                    </div>
                  ) : (
                    <LegacyImage
                      key={k + title}
                      width={e.width}
                      height={e.height}
                      src={isSVG ? svgUrl : e.secure_url}
                      layout='responsive'
                      alt={altText || `seed ${title}`}
                      loader={cloudinaryLoader}
                    />
                  );
                }
                return (
                  <div
                    key={k + title}
                    className='picture with-background'
                    style={{ backgroundImage: `url(${e.url})` }}
                  >
                    <LegacyImage
                      width={e.width}
                      height={e.height}
                      src={isSVG ? svgUrl : e.secure_url}
                      layout='responsive'
                      alt={altText || `seed ${title}`}
                      loader={cloudinaryLoader}
                    />
                  </div>
                );
              })}
            {playbackId ? (
              <MuxPlayer
                playbackId={playbackId}
                autoPlay
                loop
                muted
                style={{ width: '100%', height: '100%' }}
              />
            ) : vimeoId ? (
              <Vimeo
                video={vimeoId}
                controls={false}
                responsive
                loop
                volume={0}
                autoplay
                autopause={false}
                paused={paused}
              />
            ) : null}
          </div>
        </div>
      </div>
      {posterText && (
        <div className='label'>
          {figTextLen > 0 ? (
            <>
              <i>{figText}</i>
              <span
                dangerouslySetInnerHTML={createMarkup(
                  posterText.substr(figTextLen),
                )}
              />
            </>
          ) : (
            <span dangerouslySetInnerHTML={createMarkup(posterText)} />
          )}
        </div>
      )}
    </div>
  );
});

type SliderElProps = {
  data: any;
  params: any;
  title: string;
};

export const SliderEl = ({ data, params, title }: SliderElProps) => {
  const slides = data?.slide || data?.fields?.slide || [];
  const cmsParams = data?.params || data?.fields?.params || {};
  const sliParams = { ...cmsParams, ...params };
  return (
    // @ts-ignore
    <SiteSwiper params={sliParams} className={getContentFieldClass(data)}>
      {slides.map((v, i) => (
        <SlideEl
          key={title + i}
          data={v}
          title={title}
          checkOnlyVisible
          withAnchor
          // @ts-ignore
          onVisible
        />
      ))}
    </SiteSwiper>
  );
};

type SlideElProps = {
  data: any;
  title: string;
  visible: boolean;
};

// @ts-ignore
export const SlideEl = withVisibleHoc((props: SlideElProps, ref) => {
  const { data, title, visible } = props;
  const elems = getContentField({ data }) || [];
  const cover = data?.fields?.cover || false;

  const getImage = () => {
    const imageLG =
      data?.fields?.background &&
      Object.values(data?.fields?.background)?.shift();
    const imageSM =
      data?.fields?.backgroundSmall &&
      Object.values(data?.fields?.backgroundSmall)?.shift();
    return isScreenLarge() ? imageLG : imageSM || imageLG;
  };

  const updImage = () => setImage(getImage());
  const updVideo = () => {
    setVimeoId(isScreenLarge() ? videoId : mobileVideoId);
    setAspectRatio(isScreenLarge() ? desktopAspectRatio : mobileAspectRatio);
    setPlaybackId(isScreenLarge() ? desktopPlaybackId : mobilePlaybackId);
  };

  const videoId = data?.fields?.videoId;
  const mobileVideoId = data?.fields?.mobileVideoId;
  const desktopPlaybackId =
    process.env.NEXT_PUBLIC_STAGING === 'true'
      ? data?.fields?.devDesktopPlaybackId
      : data?.fields?.desktopPlaybackId;
  const mobilePlaybackId =
    process.env.NEXT_PUBLIC_STAGING === 'true'
      ? data?.fields?.devMobilePlaybackId
      : data?.fields?.mobilePlaybackId;
  const mobileAspectRatio = data?.fields?.mobileAspectRatio || '1:1';
  const desktopAspectRatio = data?.fields?.desktopAspectRatio || '16:9';
  const [playbackId, setPlaybackId] = useState(mobilePlaybackId);
  const [aspectRatio, setAspectRatio] = useState(mobileAspectRatio);

  const [image, setImage] = useState(null);
  const [vimeoId, setVimeoId] = useState(null);
  const [paused, setPaused] = useState(true);

  const style =
    (image && cover && { backgroundImage: `url(${image.url})` }) || {};

  useEffect(() => {
    if (visible) {
      setPaused(false);
    }
  }, [visible, paused]);

  useEffect(() => {
    updVideo();
    updImage();

    window.addEventListener('site_resize', updImage);
    window.addEventListener('site_resize', updVideo);
    return () => {
      window.removeEventListener('site_resize', updImage);
      window.removeEventListener('site_resize', updVideo);
    };
  }, []);

  return (
    // @ts-ignore
    <div data-title={title} className='swiper-slide' ref={ref}>
      <div className='banner'>
        <div className='container'>
          <div className='media' style={style}>
            {image && !vimeoId && !playbackId && !cover && (
              <LegacyImage
                width={image.width}
                height={image.height}
                src={image.secure_url}
                layout='responsive'
                alt={title || `seed ${title}`}
                loader={cloudinaryLoader}
              />
            )}
            {playbackId ? (
              <MuxPlayer
                playbackId={playbackId}
                autoPlay
                loop
                muted
                style={{
                  width: '100%',
                  height: '100%',
                  aspectRatio: aspectRatio,
                }}
              />
            ) : vimeoId ? (
              <Vimeo
                video={vimeoId}
                controls={false}
                responsive
                loop
                volume={0}
                autoplay
                paused={paused}
              />
            ) : null}
          </div>
          <div className='text'>
            <div className='container'>
              {elems.map((v, i) => (
                // @ts-ignore
                <Field key={title + i} data={v} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

type SmallElProps = {
  data: any;
  title: string;
};

export const SmallEl = ({ data, title }: SmallElProps) => {
  const content = data?.paragraph || data?.fields?.paragraph || null;
  return (
    <div
      data-title={title}
      data-type='small'
      className={getContentFieldClass(data)}
      // dangerouslySetInnerHTML={createMarkup(documentToHtmlString(content))}
    >
      {/* @ts-ignore */}
      <RichTextRenderer content={content} />
    </div>
  );
};

type TableElProps = {
  data: any;
  title: string;
  firstRowHeader?: boolean;
  firstColumnHeader?: boolean;
};

export const TableEl = ({
  data,
  title,
  firstRowHeader,
  firstColumnHeader,
}: TableElProps) => {
  const table = data?.content || data?.fields?.content || [];
  const tableRef = useRef(undefined);
  const [activeBullet, setActiveBullets] = useState(0);

  const isMobileResponsive = useMemo(
    () => data?.fields?.class?.includes('with-bullets'),
    [data],
  );

  const bulletsArr = useMemo(() => {
    const arr = [];
    if (table[0]) {
      const tbLen = table[0].length;
      for (let i = 0; i < tbLen; i += 1) {
        arr.push(i);
      }
    }
    return arr;
  }, [table]);

  useEffect(() => {
    const table = tableRef.current;

    const handleScroll = () => {
      let index = 0;

      // @ts-ignore
      const { scrollWidth } = table;

      // @ts-ignore
      const { scrollLeft } = table;

      // @ts-ignore
      const { width } = table.getBoundingClientRect();

      Array.from(
        // @ts-ignore
        table.querySelectorAll('table tr')[0].querySelectorAll('td'),
      ).forEach((item, i) => {
        if (
          i &&
          // @ts-ignore
          table.getBoundingClientRect().left >=
            // @ts-ignore
            item.getBoundingClientRect().left
        ) {
          index = i;
        }
        // handle scroll end
        if (scrollWidth - scrollLeft - width === 0) {
          const ttr =
            // @ts-ignore
            table.querySelectorAll('table tr') &&
            // @ts-ignore
            table.querySelectorAll('table tr')[0];
          const tdLen = ttr.querySelectorAll('td')?.length || 0;
          index = tdLen - 1;
        }
      });
      if (index >= 0) setActiveBullets(index);
    };

    if (isMobileResponsive) {
      // @ts-ignore
      table.addEventListener('scroll', handleScroll, { passive: true });
    }

    // @ts-ignore
    return () => table.removeEventListener('scroll', handleScroll);
  }, [tableRef, isMobileResponsive]);

  return (
    <div
      data-title={title}
      data-type='table'
      className={getContentFieldClass(data)}
    >
      <table ref={tableRef} className='tableel'>
        <tbody>
          {Array.isArray(table) &&
            table.map((tr, i) => (
              <tr key={i + title}>
                {Array.isArray(tr) &&
                  tr.map((td, k) => {
                    if (
                      (firstRowHeader && i === 0) ||
                      (firstColumnHeader && k === 0)
                    ) {
                      const scope = firstRowHeader && i === 0 ? 'col' : 'row';
                      return (
                        <TableHeader
                          key={i + k + title}
                          scope={scope}
                          dangerouslySetInnerHTML={createMarkup(
                            td.length === 0
                              ? '(this column header is blank)'
                              : td,
                          )}
                          className={
                            td.length === 0
                              ? 'screenreader-only-table-header'
                              : undefined
                          }
                        />
                      );
                    }
                    return (
                      <td
                        key={i + k + title}
                        dangerouslySetInnerHTML={createMarkup(td)}
                      />
                    );
                  })}
              </tr>
            ))}
        </tbody>
      </table>

      {isMobileResponsive && (
        <div className='bullets'>
          {bulletsArr.map((_, i) => (
            <div
              className={`bullet ${activeBullet === i ? 'active' : ''}`}
              key={i}
            />
          ))}
        </div>
      )}
    </div>
  );
};
